/* eslint-disable react/prop-types */
/* eslint-disable prettier/prettier */
import { useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import axios from "axios";
import API_ENDPOINTS from "config/api";
import { exportTableToPDF, exportMultipleSheetsToExcel } from "utils/exportHelpers";
import CircularProgress from "@mui/material/CircularProgress";

export default function ReportesPersonalizados() {
  const [tipoReporte, setTipoReporte] = useState("ventas");
  const [formato, setFormato] = useState("pdf");
  const [periodoInicio, setPeriodoInicio] = useState("2024-01-01");
  const [periodoFin, setPeriodoFin] = useState("2024-06-30");
  const [empresa, setEmpresa] = useState("todas");
  const [sucursal, setSucursal] = useState("todas");
  const [loading, setLoading] = useState(false);

  // Filtros seleccionables
  const [filtros, setFiltros] = useState({
    incluirGraficas: true,
    incluirTablas: true,
    incluirKPIs: true,
    incluirResumen: true,
    comparativoAnual: false,
    detalleTransacciones: false,
  });

  // Métricas disponibles por tipo de reporte
  const metricasDisponibles = {
    ventas: [
      { id: "ventasTotales", label: "Ventas Totales", checked: true },
      { id: "numTransacciones", label: "Número de Transacciones", checked: true },
      { id: "ticketPromedio", label: "Ticket Promedio", checked: true },
      { id: "ventasPorProducto", label: "Ventas por Producto", checked: true },
      { id: "ventasPorVendedor", label: "Ventas por Vendedor", checked: false },
      { id: "ventasPorCanal", label: "Ventas por Canal", checked: false },
      { id: "tendenciaVentas", label: "Tendencia de Ventas", checked: true },
      { id: "clientesNuevos", label: "Clientes Nuevos", checked: false },
    ],
    financiero: [
      { id: "balanceGeneral", label: "Balance General", checked: true },
      { id: "estadoResultados", label: "Estado de Resultados", checked: true },
      { id: "flujoCaja", label: "Flujo de Caja", checked: true },
      { id: "ratiosFinancieros", label: "Ratios Financieros", checked: true },
      { id: "ingresosMensuales", label: "Ingresos Mensuales", checked: false },
      { id: "gastosMensuales", label: "Gastos Mensuales", checked: false },
      { id: "utilidadMensual", label: "Utilidad Mensual", checked: true },
      { id: "cuentasPorCobrar", label: "Cuentas por Cobrar", checked: false },
    ],
    inventario: [
      { id: "valorInventario", label: "Valor Total de Inventario", checked: true },
      { id: "stockPorCategoria", label: "Stock por Categoría", checked: true },
      { id: "movimientos", label: "Movimientos de Inventario", checked: true },
      { id: "rotacion", label: "Rotación de Inventario", checked: true },
      { id: "stockBajo", label: "Productos con Stock Bajo", checked: true },
      { id: "bajaRotacion", label: "Productos de Baja Rotación", checked: false },
      { id: "valorPorAlmacen", label: "Valor por Almacén", checked: false },
      { id: "transferencias", label: "Transferencias entre Almacenes", checked: false },
    ],
    logistica: [
      { id: "entregasRealizadas", label: "Entregas Realizadas", checked: true },
      { id: "entregasATiempo", label: "Entregas a Tiempo", checked: true },
      { id: "costosLogisticos", label: "Costos Logísticos", checked: true },
      { id: "eficienciaRutas", label: "Eficiencia de Rutas", checked: true },
      { id: "desempenoConductores", label: "Desempeño de Conductores", checked: false },
      { id: "entregasPorZona", label: "Entregas por Zona", checked: false },
      { id: "vehiculosActivos", label: "Vehículos Activos", checked: false },
      { id: "costosPorKm", label: "Costos por Kilómetro", checked: false },
    ],
    rrhh: [
      { id: "empleadosActivos", label: "Empleados Activos", checked: true },
      { id: "nominaTotal", label: "Nómina Total", checked: true },
      { id: "ausentismo", label: "Ausentismo", checked: true },
      { id: "rotacionPersonal", label: "Rotación de Personal", checked: true },
    ],
    general: [
      { id: "resumenGeneral", label: "Resumen General", checked: true },
      { id: "alertas", label: "Alertas Activas", checked: true },
    ],
  };

  const [metricas, setMetricas] = useState(metricasDisponibles.ventas);

  const handleTipoReporteChange = (value) => {
    setTipoReporte(value);
    setMetricas(metricasDisponibles[value] || []);
  };

  const handleMetricaChange = (index) => {
    const newMetricas = [...metricas];
    newMetricas[index].checked = !newMetricas[index].checked;
    setMetricas(newMetricas);
  };

  const handleFiltroChange = (name) => {
    setFiltros((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleGenerar = async () => {
    setLoading(true);
    try {
      let endpoint = "";
      switch (tipoReporte) {
        case "ventas":
          endpoint = API_ENDPOINTS.REPORTES.VENTAS_OPERATIVO;
          break;
        case "financiero":
          endpoint = API_ENDPOINTS.REPORTES.FINANCIEROS;
          break;
        case "inventario":
          endpoint = API_ENDPOINTS.REPORTES.INVENTARIO_OPERATIVO;
          break;
        case "logistica":
          endpoint = API_ENDPOINTS.LOGISTICA.REPORTES;
          break;
        case "rrhh":
          endpoint = API_ENDPOINTS.RRHH.DASHBOARD;
          break;
        case "general":
          endpoint = API_ENDPOINTS.REPORTES.DASHBOARD_GENERAL;
          break;
        default:
          endpoint = API_ENDPOINTS.REPORTES.DASHBOARD_GENERAL;
      }

      console.log(`Fetching data from ${endpoint}...`);
      const response = await axios.get(endpoint, {
        params: {
          fecha_inicio: periodoInicio,
          fecha_fin: periodoFin,
          empresa_id: empresa !== "todas" ? empresa : null,
          sucursal_id: sucursal !== "todas" ? sucursal : null,
        },
      });

      const data = response.data;
      console.log("Data received:", data);

      // Preparar datos para exportación
      // Extraer KPIs comunes
      const kpis = [];
      if (data.kpis) {
        Object.entries(data.kpis).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            // Handle objects like { total: 100, trend: +5% }
            kpis.push({ Metric: key, Value: value.total || value.count || JSON.stringify(value) });
          } else {
            kpis.push({ Metric: key, Value: value });
          }
        });
      }

      // Generar Archivo
      const fileName = `Reporte_${tipoReporte}_${periodoInicio}_${periodoFin}`;

      if (formato === "pdf") {
        // Exportar KPIs a PDF como tabla resumen
        exportTableToPDF(
          `Reporte de ${tipoReporte.toUpperCase()}`,
          [
            { header: "Métrica", dataKey: "Metric" },
            { header: "Valor", dataKey: "Value" },
          ],
          kpis,
          `${fileName}.pdf`
        );
      } else if (formato === "excel" || formato === "csv") {
        // Exportar múltiples hojas si hay datos detallados
        const sheets = [{ name: "Resumen KPIs", data: kpis }];

        // Agregar hojas extra según disponibilidad de datos
        if (data.productos && Array.isArray(data.productos)) sheets.push({ name: "Productos", data: data.productos });
        if (data.ventas && Array.isArray(data.ventas)) sheets.push({ name: "Ventas", data: data.ventas });
        if (data.entregas_dia && Array.isArray(data.entregas_dia)) sheets.push({ name: "Entregas", data: data.entregas_dia });

        exportMultipleSheetsToExcel(sheets, `${fileName}.xlsx`);
      } else {
        alert("Formato JSON no soportado aún en esta demo.");
      }

      alert("Reporte generado exitosamente.");

    } catch (error) {
      console.error("Error al generar reporte:", error);
      alert("Error al generar el reporte. Verifique la consola para más detalles.");
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarPlantilla = () => {
    const plantilla = {
      tipoReporte,
      formato,
      filtros,
      metricas, // Save checked state
    };
    localStorage.setItem("reporte_plantilla_custom", JSON.stringify(plantilla));
    alert("Plantilla guardada localmente exitosamente.");
  };

  const handleCargarPlantilla = () => {
    const saved = localStorage.getItem("reporte_plantilla_custom");
    if (saved) {
      const p = JSON.parse(saved);
      setTipoReporte(p.tipoReporte);
      setFormato(p.formato);
      setFiltros(p.filtros);
      // Update metricas based on type if needed, or use saved ones if type matches
      if (p.tipoReporte === tipoReporte) {
        setMetricas(p.metricas);
      } else {
        setMetricas(metricasDisponibles[p.tipoReporte]);
      }
      alert("Plantilla cargada exitosamente.");
    } else {
      alert("No hay plantilla guardada disponible.");
    }
  };

  return (
    <MDBox>
      {/* Título */}
      <Card sx={{ mb: 3 }}>
        <MDBox p={3}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="medium">
              Constructor de Reportes Personalizados
            </MDTypography>
            <MDBox display="flex" gap={1}>
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                onClick={handleCargarPlantilla}
              >
                <Icon>folder_open</Icon>&nbsp;Cargar Plantilla
              </MDButton>
              <MDButton
                variant="outlined"
                color="primary"
                size="small"
                onClick={handleGuardarPlantilla}
              >
                <Icon>save</Icon>&nbsp;Guardar Plantilla
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>

      <Grid container spacing={3}>
        {/* Panel de Configuración */}
        <Grid item xs={12} md={4}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Configuración del Reporte
              </MDTypography>

              {/* Tipo de Reporte */}
              <MDBox mb={2}>
                <MDInput
                  select
                  label="Tipo de Reporte"
                  value={tipoReporte}
                  onChange={(e) => handleTipoReporteChange(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="ventas">Reporte de Ventas</MenuItem>
                  <MenuItem value="financiero">Reporte Financiero</MenuItem>
                  <MenuItem value="inventario">Reporte de Inventario</MenuItem>
                  <MenuItem value="logistica">Reporte de Logística</MenuItem>
                  <MenuItem value="rrhh">Reporte de RRHH</MenuItem>
                  <MenuItem value="general">Reporte General</MenuItem>
                </MDInput>
              </MDBox>

              {/* Formato */}
              <MDBox mb={2}>
                <MDInput
                  select
                  label="Formato de Exportación"
                  value={formato}
                  onChange={(e) => setFormato(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="pdf">PDF</MenuItem>
                  <MenuItem value="excel">Excel (.xlsx)</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="json">JSON</MenuItem>
                </MDInput>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {/* Período */}
              <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                Período
              </MDTypography>
              <MDBox mb={2}>
                <MDInput
                  type="date"
                  label="Fecha Inicio"
                  value={periodoInicio}
                  onChange={(e) => setPeriodoInicio(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="date"
                  label="Fecha Fin"
                  value={periodoFin}
                  onChange={(e) => setPeriodoFin(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {/* Empresa y Sucursal */}
              <MDBox mb={2}>
                <MDInput
                  select
                  label="Empresa"
                  value={empresa}
                  onChange={(e) => setEmpresa(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="todas">Todas las Empresas</MenuItem>
                  <MenuItem value="empresa1">Empresa Principal S.A.</MenuItem>
                  <MenuItem value="empresa2">Sucursal Comercial Ltda.</MenuItem>
                  <MenuItem value="empresa3">Distribuidora Norte S.A.</MenuItem>
                </MDInput>
              </MDBox>

              <MDBox mb={2}>
                <MDInput
                  select
                  label="Sucursal"
                  value={sucursal}
                  onChange={(e) => setSucursal(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="todas">Todas las Sucursales</MenuItem>
                  <MenuItem value="central">Sucursal Central</MenuItem>
                  <MenuItem value="norte">Sucursal Norte</MenuItem>
                  <MenuItem value="sur">Sucursal Sur</MenuItem>
                  <MenuItem value="este">Sucursal Este</MenuItem>
                  <MenuItem value="oeste">Sucursal Oeste</MenuItem>
                </MDInput>
              </MDBox>

              <Divider sx={{ my: 2 }} />

              {/* Opciones Adicionales */}
              <MDTypography variant="button" fontWeight="bold" mb={1} display="block">
                Opciones del Reporte
              </MDTypography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtros.incluirGraficas}
                      onChange={() => handleFiltroChange("incluirGraficas")}
                    />
                  }
                  label="Incluir Gráficas"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtros.incluirTablas}
                      onChange={() => handleFiltroChange("incluirTablas")}
                    />
                  }
                  label="Incluir Tablas"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtros.incluirKPIs}
                      onChange={() => handleFiltroChange("incluirKPIs")}
                    />
                  }
                  label="Incluir KPIs"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtros.incluirResumen}
                      onChange={() => handleFiltroChange("incluirResumen")}
                    />
                  }
                  label="Incluir Resumen Ejecutivo"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtros.comparativoAnual}
                      onChange={() => handleFiltroChange("comparativoAnual")}
                    />
                  }
                  label="Comparativo Anual"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={filtros.detalleTransacciones}
                      onChange={() => handleFiltroChange("detalleTransacciones")}
                    />
                  }
                  label="Detalle de Transacciones"
                />
              </FormGroup>

              <Divider sx={{ my: 2 }} />

              {/* Botón Generar */}
              <MDButton
                variant="gradient"
                color="success"
                fullWidth
                size="large"
                onClick={handleGenerar}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="white" />
                ) : (
                  <>
                    <Icon>assessment</Icon>&nbsp;Generar Reporte
                  </>
                )}
              </MDButton>
            </MDBox>
          </Card>
        </Grid>

        {/* Panel de Métricas */}
        <Grid item xs={12} md={8}>
          <Card>
            <MDBox p={3}>
              <MDTypography variant="h6" fontWeight="medium" mb={2}>
                Selección de Métricas e Indicadores
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={3}>
                Seleccione las métricas que desea incluir en el reporte personalizado
              </MDTypography>

              <Grid container spacing={2}>
                {metricas.map((metrica, index) => (
                  <Grid item xs={12} sm={6} key={metrica.id}>
                    <Card
                      sx={{
                        cursor: "pointer",
                        border: "1px solid",
                        borderColor: metrica.checked ? "success.main" : "grey.300",
                        backgroundColor: metrica.checked ? "success.light" : "transparent",
                        transition: "all 0.3s",
                        "&:hover": {
                          borderColor: "success.main",
                          transform: "translateY(-2px)",
                        },
                      }}
                      onClick={() => handleMetricaChange(index)}
                    >
                      <MDBox p={2} display="flex" alignItems="center">
                        <Checkbox
                          checked={metrica.checked}
                          onChange={() => handleMetricaChange(index)}
                        />
                        <MDTypography variant="button" fontWeight="medium" ml={1}>
                          {metrica.label}
                        </MDTypography>
                      </MDBox>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Resumen de Selección */}
              <MDBox>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Resumen de Configuración
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDBox p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Tipo de Reporte:</strong> {tipoReporte}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Formato:</strong> {formato.toUpperCase()}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Período:</strong> {periodoInicio} a {periodoFin}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Empresa:</strong> {empresa}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Sucursal:</strong> {sucursal}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox p={2} sx={{ backgroundColor: "#f5f5f5", borderRadius: 2 }}>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Métricas seleccionadas:</strong>{" "}
                        {metricas.filter((m) => m.checked).length} de {metricas.length}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Incluir Gráficas:</strong> {filtros.incluirGraficas ? "Sí" : "No"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Incluir Tablas:</strong> {filtros.incluirTablas ? "Sí" : "No"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Incluir KPIs:</strong> {filtros.incluirKPIs ? "Sí" : "No"}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" display="block">
                        <strong>Comparativo Anual:</strong> {filtros.comparativoAnual ? "Sí" : "No"}
                      </MDTypography>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Plantillas Predefinidas */}
              <Divider sx={{ my: 3 }} />
              <MDBox>
                <MDTypography variant="h6" fontWeight="medium" mb={2}>
                  Plantillas Predefinidas
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDButton variant="outlined" color="info" fullWidth>
                      Reporte Ejecutivo Mensual
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDButton variant="outlined" color="primary" fullWidth>
                      Análisis Trimestral
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDButton variant="outlined" color="success" fullWidth>
                      Reporte Anual Completo
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDButton variant="outlined" color="warning" fullWidth>
                      Dashboard Operativo
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDButton variant="outlined" color="error" fullWidth>
                      Análisis de Rentabilidad
                    </MDButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <MDButton variant="outlined" color="dark" fullWidth>
                      Reporte de Auditoría
                    </MDButton>
                  </Grid>
                </Grid>
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}
